/**
 * location-picker.js
 *
 * Selector global de sucursal para el header.
 * Depende de location-inventory.js (debe cargarse antes).
 * Usa window.__LI para acceder a GeoService y haversineDistanceKm.
 *
 * Custom Element: <location-picker>
 */

(function () {
  if (window.__locationPickerLoaded) return;
  window.__locationPickerLoaded = true;

  const LI_STORAGE_KEY_LOCATION = 'li_selected_location';
  const LI_STORAGE_KEY_AUTO     = 'li_location_auto';

  if (!customElements.get('location-picker')) {
    customElements.define(
      'location-picker',
      class LocationPicker extends HTMLElement {

        connectedCallback() {
          this._open      = false;
          this._locations = [];
          this._sorted    = [];

          const saved = this._getSavedLocationId();
          if (saved) {
            this._selectedId = saved;
          } else if (this.dataset.defaultLocationId) {
            // Sin historial → aplicar default desde Liquid inmediatamente
            this._selectedId = this.dataset.defaultLocationId;
            try { localStorage.setItem(LI_STORAGE_KEY_LOCATION, this._selectedId); } catch {}
            try { localStorage.setItem(LI_STORAGE_KEY_AUTO, '1'); } catch {}
          } else {
            this._selectedId = null;
          }

          // Leer lista de sucursales embebida por location-picker.liquid
          const dataEl = document.getElementById('li-all-locations-data');
          if (dataEl) {
            try {
              this._locations = JSON.parse(dataEl.textContent);
              // Guardar en caché para páginas donde Liquid no puede embedir la data
              try { localStorage.setItem('li_locations_cache', dataEl.textContent); } catch {}
            } catch {
              // datos inválidos → intentar caché
            }
          }

          // Fallback 1: caché de localStorage
          if (this._locations.length === 0) {
            try {
              const cached = localStorage.getItem('li_locations_cache');
              if (cached) this._locations = JSON.parse(cached);
            } catch {}
          }

          // Render inicial (con lo que haya hasta ahora)
          this._sorted = this._locations;
          this._setDefaultLocation();
          this._render();

          // Fallback 2: fetch al servidor si todavía no hay datos
          if (this._locations.length === 0) {
            const variantId = this.dataset.variantId;
            const rootUrl   = (this.dataset.rootUrl || '/').replace(/\/?$/, '/');
            if (variantId) {
              fetch(`${rootUrl}variants/${variantId}/?section_id=location-inventory`)
                .then((r) => r.ok ? r.text() : Promise.reject())
                .then((html) => {
                  const doc    = new DOMParser().parseFromString(html, 'text/html');
                  const dataEl = doc.querySelector('#location-inventory-data');
                  if (!dataEl) return;
                  const data = JSON.parse(dataEl.textContent);
                  if (data.locations && data.locations.length > 0) {
                    this._locations = data.locations;
                    try { localStorage.setItem('li_locations_cache', JSON.stringify(this._locations)); } catch {}
                    this._sorted = this._locations;
                    this._setDefaultLocation();
                    this._render();
                  }
                })
                .catch(() => {});
            }
          }

          // Detectar ubicación y reordenar por distancia
          const LI = window.__LI || {};
          if (LI.GeoService) {
            const doGeo = () => {
              LI.GeoService.getUserCoords()
                .then((coords) => {
                  if (!coords) return;
                  const dist          = LI.haversineDistanceKm;
                  const withCoords    = this._locations.filter((l) => l.lat !== null && l.lng !== null);
                  const withoutCoords = this._locations.filter((l) => l.lat === null || l.lng === null);

                  this._sorted = [
                    ...withCoords
                      .map((l) => ({ ...l, distanceKm: dist(coords.lat, coords.lng, l.lat, l.lng) }))
                      .sort((a, b) => a.distanceKm - b.distanceKm),
                    ...withoutCoords,
                  ];

                  if (this._isAutoSelection() && this._sorted.length > 0) {
                    this._selectLocation(this._sorted[0].id, { auto: true });
                  } else {
                    this._render();
                  }
                })
                .catch(() => {});
            };

            doGeo();
          }

          // Escucha cuando el widget de inventario cambia la sucursal
          if (typeof subscribe === 'function') {
            this._unsub = subscribe('location-inventory:location-changed', ({ locationId }) => {
              this._selectedId = String(locationId);
              this._render();
            });
          }

          // Cierra el dropdown al hacer clic fuera
          this._outsideClick = (e) => {
            if (!this.contains(e.target)) {
              this._open = false;
              this._render();
            }
          };
          document.addEventListener('click', this._outsideClick);
        }

        disconnectedCallback() {
          if (this._unsub)         this._unsub();
          if (this._outsideClick)  document.removeEventListener('click', this._outsideClick);
        }

        _getSavedLocationId() {
          try { return localStorage.getItem(LI_STORAGE_KEY_LOCATION); } catch { return null; }
        }

        _setDefaultLocation() {
          if (this._selectedId) return; // Ya hay una guardada → no sobreescribir

          // Preferir el ID pasado por Liquid (server-side, sin riesgo de encoding)
          const defaultId = this.dataset.defaultLocationId;
          if (defaultId) {
            this._selectLocation(defaultId, { auto: true });
            return;
          }

          // Fallback: buscar por ID
          const def = this._locations.find((l) => String(l.id) === '81185505590');
          if (def) {
            this._selectLocation(def.id, { auto: true });
          }
        }

        _isAutoSelection() {
          try { return localStorage.getItem(LI_STORAGE_KEY_AUTO) === '1'; } catch { return false; }
        }

        _selectLocation(locationId, { auto = false } = {}) {
          this._selectedId = String(locationId);
          try { localStorage.setItem(LI_STORAGE_KEY_LOCATION, locationId); } catch {}
          try {
            if (auto) {
              localStorage.setItem(LI_STORAGE_KEY_AUTO, '1');
            } else {
              localStorage.removeItem(LI_STORAGE_KEY_AUTO);
            }
          } catch {}

          // Guardar como atributo del carrito para que quede registrado en el pedido
          const location = this._sorted.find((l) => String(l.id) === String(locationId));
          if (location) {
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: {
                  'Sucursal preferida': location.name,
                  'Sucursal preferida ID': String(locationId),
                }
              }),
            }).catch(() => {}); // Fallo silencioso — no bloquea la experiencia
          }

          if (typeof publish === 'function') {
            publish('location-inventory:location-changed', { locationId });
          }
          this._open = false;
          this._render();
        }

        _render() {
          if (this._sorted.length === 0) {
            this.style.display = 'none';
            return;
          }
          this.style.display = '';

          const selected   = this._sorted.find((l) => String(l.id) === String(this._selectedId));
          const storeName  = selected ? selected.name : 'Seleccioná tu sucursal';
          const address    = selected ? (selected.address || '') : '';
          const eyebrow    = 'Tu tienda más cercana';

          const selDistLabel = selected && selected.distanceKm != null
            ? (selected.distanceKm < 1
                ? `${Math.round(selected.distanceKm * 1000)} m`
                : `${selected.distanceKm.toFixed(1)} km`)
            : '';
          const detail = [address, selDistLabel].filter(Boolean).join('\u2003');

          const rows = this._sorted.map((loc) => {
            const isSelected = String(loc.id) === String(this._selectedId);
            const distLabel  = loc.distanceKm != null
              ? (loc.distanceKm < 1
                  ? `${Math.round(loc.distanceKm * 1000)} m`
                  : `${loc.distanceKm.toFixed(1)} km`)
              : '';

            return `
              <button class="lp-option${isSelected ? ' lp-option--selected' : ''}"
                      data-location-id="${loc.id}">
                <span class="lp-option__name">${loc.name}</span>
                ${distLabel ? `<span class="lp-option__distance">${distLabel}</span>` : ''}
                <span class="lp-option__address">${loc.address}</span>
              </button>
            `;
          }).join('');

          this.innerHTML = `
            <button class="lp-trigger focus-inset"
                    aria-expanded="${this._open}"
                    aria-label="Seleccionar sucursal">
              <svg class="lp-pin-icon" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="1.5"
                   stroke-linecap="round" stroke-linejoin="round"
                   aria-hidden="true">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                <circle cx="12" cy="9" r="2.5"/>
              </svg>
              <span class="lp-trigger__eyebrow lp-trigger__eyebrow--mobile">${eyebrow}</span>
              <span class="lp-trigger__pill">
                <span class="lp-trigger__text">
                  <span class="lp-trigger__eyebrow lp-trigger__eyebrow--desktop">${eyebrow}</span>
                  <span class="lp-trigger__name">${storeName}</span>
                  ${detail ? `<span class="lp-trigger__detail">${detail}</span>` : ''}
                </span>
                <svg class="lp-chevron" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="1.5"
                     aria-hidden="true">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </span>
            </button>

            <div class="lp-dropdown${this._open ? '' : ' lp-dropdown--hidden'}"
                 role="listbox" aria-label="Sucursales">
              ${rows}
            </div>
          `;

          // Toggle del botón principal
          this.querySelector('.lp-trigger').addEventListener('click', (e) => {
            e.stopPropagation();
            this._open = !this._open;
            this._render();
            // Safari requiere gesto del usuario para mostrar el dialog de ubicación
            if (this._pendingGeo) {
              const fn = this._pendingGeo;
              this._pendingGeo = null;
              fn();
            }
          });

          // Selección de sucursal
          this.querySelectorAll('.lp-option').forEach((btn) => {
            btn.addEventListener('click', (e) => {
              e.stopPropagation();
              this._selectLocation(btn.dataset.locationId);
            });
          });
        }
      }
    );
  }
})();
